<template>
    <div class="p-4">
        <!-- Stats -->
        <div class="row g-3 mb-3">
            <div class="col-12 col-md-4">
                <StatCard
                    title="Avisos abiertos"
                    :value="avisosTotal"
                    icon="bi-bell"
                    :loading="loadingAvisos"
                />
            </div>
            <div class="col-12 col-md-4">
                <StatCard
                    title="Citas hoy"
                    :value="citasHoy"
                    icon="bi-calendar-event"
                    :loading="loadingAgenda"
                />
            </div>
            <div class="col-12 col-md-4">
                <StatCard
                    title="Partes abiertos"
                    :value="partesTotal"
                    icon="bi-file-text"
                    :loading="loadingPartes"
                />
            </div>
        </div>

        <!-- Columnas de detalle -->
        <div class="row g-3">
            <!-- Avisos -->
            <div class="col-12 col-md-4">
                <div class="card h-100">
                    <div class="card-header d-flex align-items-center justify-content-between py-2">
                        <span class="fw-semibold small">Mis Avisos</span>
                        <RouterLink :to="{ name: 'mis-avisos' }" class="small text-muted text-decoration-none">
                            Ver todos
                        </RouterLink>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="loadingAvisos" class="text-center py-5">
                            <div class="spinner-border spinner-border-sm text-secondary" role="status">
                                <span class="visually-hidden">Cargando...</span>
                            </div>
                        </div>
                        <div v-else-if="errorAvisos" class="alert alert-danger m-3 small">{{ errorAvisos }}</div>
                        <div v-else-if="avisosItems.length === 0" class="text-center text-muted py-5 small">
                            Sin avisos abiertos.
                        </div>
                        <div v-else>
                            <template v-for="grupo in avisosAgrupados" :key="grupo.clienteNombre">
                                <div
                                    class="px-3 py-2 border-bottom bg-light d-flex align-items-center justify-content-between"
                                    style="cursor: pointer;"
                                    @click="avisosExpandidos[grupo.clienteNombre] = !avisosExpandidos[grupo.clienteNombre]"
                                >
                                    <span class="small fw-semibold text-truncate">{{ grupo.clienteNombre }}</span>
                                    <div class="d-flex align-items-center gap-2 flex-shrink-0 ms-2">
                                        <span class="badge bg-secondary rounded-pill">{{ grupo.avisos.length }}</span>
                                        <i class="bi small" :class="avisosExpandidos[grupo.clienteNombre] ? 'bi-chevron-up' : 'bi-chevron-down'" />
                                    </div>
                                </div>
                                <div v-show="avisosExpandidos[grupo.clienteNombre]">
                                    <RouterLink
                                        v-for="aviso in grupo.avisos"
                                        :key="aviso.idAviso"
                                        :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }"
                                        class="d-block px-3 py-2 border-bottom text-decoration-none text-body"
                                    >
                                        <div class="text-muted small">#{{ aviso.idAviso }} · {{ formatFecha(aviso.fecha) }}</div>
                                        <div class="small text-clamp-2">{{ aviso.descripcion }}</div>
                                    </RouterLink>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Agenda -->
            <div class="col-12 col-md-4">
                <div class="card h-100">
                    <div class="card-header d-flex align-items-center justify-content-between py-2">
                        <span class="fw-semibold small">Mi Agenda</span>
                        <RouterLink :to="{ name: 'mi-agenda' }" class="small text-muted text-decoration-none">
                            Ver agenda
                        </RouterLink>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="loadingAgenda" class="text-center py-5">
                            <div class="spinner-border spinner-border-sm text-secondary" role="status">
                                <span class="visually-hidden">Cargando...</span>
                            </div>
                        </div>
                        <div v-else-if="errorAgenda" class="alert alert-danger m-3 small">{{ errorAgenda }}</div>
                        <div v-else-if="agendaItems.length === 0" class="text-center text-muted py-5 small">
                            Sin citas esta semana.
                        </div>
                        <div v-else>
                            <template v-for="grupo in agendaAgrupada" :key="grupo.label">
                                <div class="px-3 pt-2 pb-1 small fw-semibold border-bottom bg-light">
                                    {{ grupo.label }}
                                </div>
                                <div
                                    v-for="item in grupo.items"
                                    :key="item.id"
                                    class="px-3 py-2 border-bottom"
                                    :class="{ 'opacity-50': item.realizada }"
                                >
                                    <div class="d-flex align-items-start justify-content-between mb-1">
                                        <span class="small fw-semibold text-truncate" :class="{ 'text-decoration-line-through text-muted': item.realizada }">{{ item.asunto ?? '(Sin asunto)' }}</span>
                                        <div class="d-flex align-items-center gap-1 ms-2 flex-shrink-0">
                                            <i v-if="item.realizada" class="bi bi-check-circle-fill text-success small" title="Realizada" />
                                            <span class="small text-muted text-nowrap">{{ formatHora(item.fechaInicio) }} – {{ formatHora(item.fechaFinal) }}</span>
                                        </div>
                                    </div>
                                    <div v-if="item.aviso" class="small text-muted">
                                        <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: item.aviso.idAviso } }" class="text-decoration-none">
                                            <i class="bi bi-bell me-1" />Aviso #{{ item.aviso.idAviso }}
                                        </RouterLink>
                                    </div>
                                    <div v-if="item.cliente" class="small text-muted">
                                        <RouterLink :to="{ name: 'cliente-detail', params: { idc: item.cliente.idc } }" class="text-decoration-none">
                                            <i class="bi bi-building me-1" />{{ item.cliente.nombreFiscal }}
                                        </RouterLink>
                                    </div>
                                    <div v-if="item.observaciones" class="small text-muted mt-1">
                                        {{ item.observaciones }}
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Partes -->
            <div class="col-12 col-md-4">
                <div class="card h-100">
                    <div class="card-header d-flex align-items-center justify-content-between py-2">
                        <span class="fw-semibold small">Mis Partes</span>
                        <RouterLink :to="{ name: 'mis-partes' }" class="small text-muted text-decoration-none">
                            Ver todos
                        </RouterLink>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="loadingPartes" class="text-center py-5">
                            <div class="spinner-border spinner-border-sm text-secondary" role="status">
                                <span class="visually-hidden">Cargando...</span>
                            </div>
                        </div>
                        <div v-else-if="errorPartes" class="alert alert-danger m-3 small">{{ errorPartes }}</div>
                        <div v-else-if="partesItems.length === 0" class="text-center text-muted py-5 small">
                            Sin partes abiertos.
                        </div>
                        <div v-else>
                            <RouterLink
                                v-for="parte in partesItems"
                                :key="parte.idParte"
                                :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }"
                                class="d-flex align-items-start justify-content-between px-3 py-2 border-bottom text-decoration-none text-body"
                            >
                                <div style="min-width: 0;">
                                    <div class="small fw-semibold text-truncate">
                                        {{ parte.cliente?.nombreFiscal || 'Sin cliente' }}
                                    </div>
                                    <div class="small text-muted">
                                        #{{ parte.idParte }}
                                        <span v-if="parte.aviso"> · Aviso #{{ parte.aviso.idAviso }}</span>
                                        · {{ formatFecha(parte.fecha) }}
                                    </div>
                                </div>
                                <span
                                    v-if="diasDesde(parte.fecha) > 0"
                                    class="badge ms-2 flex-shrink-0"
                                    :class="badgeClass(diasDesde(parte.fecha))"
                                >
                                    {{ diasDesde(parte.fecha) }}d
                                </span>
                            </RouterLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { usePageHeader } from '@/composables/usePageHeader'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'
import { misAvisosApi } from '@/api/misAvisosApi'
import { misPartesApi } from '@/api/misPartesApi'
import { miAgendaApi } from '@/api/miAgendaApi'
import type { Aviso } from '@/types/aviso'
import type { Parte } from '@/types/parte'
import type { Agenda } from '@/types/agenda'
import StatCard from '@/components/StatCard.vue'

usePageHeader('Dashboard')
useBreadcrumbs([])

const avisosItems = ref<Aviso[]>([])
const avisosTotal = ref(0)
const loadingAvisos = ref(true)
const errorAvisos = ref<string | null>(null)
const avisosExpandidos = ref<Record<string, boolean>>({})

const partesItems = ref<Parte[]>([])
const partesTotal = ref(0)
const loadingPartes = ref(true)
const errorPartes = ref<string | null>(null)

const agendaItems = ref<Agenda[]>([])
const loadingAgenda = ref(true)
const errorAgenda = ref<string | null>(null)

const citasHoy = computed(() => {
    const todayStr = new Date().toISOString().slice(0, 10)
    return agendaItems.value.filter((a) => a.fechaInicio.slice(0, 10) === todayStr).length
})

const avisosAgrupados = computed(() => {
    const map = new Map<string, Aviso[]>()
    for (const aviso of avisosItems.value) {
        const nombre = aviso.cliente?.nombreFiscal || 'Sin cliente'
        if (!map.has(nombre)) map.set(nombre, [])
        map.get(nombre)!.push(aviso)
    }
    return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b, 'es'))
        .map(([clienteNombre, avisos]) => ({ clienteNombre, avisos }))
})

const agendaAgrupada = computed(() => {
    const today = new Date()
    const todayStr = today.toISOString().slice(0, 10)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    const tomorrowStr = tomorrow.toISOString().slice(0, 10)

    const map = new Map<string, Agenda[]>()
    for (const item of agendaItems.value) {
        const day = item.fechaInicio.slice(0, 10)
        if (!map.has(day)) map.set(day, [])
        map.get(day)!.push(item)
    }
    return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([day, items]) => ({
            label: day === todayStr ? 'Hoy' : day === tomorrowStr ? 'Mañana' : formatFechaCorta(day),
            items,
        }))
})

function diasDesde(fecha: string): number {
    return Math.floor((Date.now() - new Date(fecha).getTime()) / (1000 * 60 * 60 * 24))
}

function badgeClass(dias: number): string {
    if (dias >= 8) return 'bg-danger'
    if (dias >= 4) return 'bg-warning text-dark'
    return 'bg-light text-muted border'
}

function formatFecha(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function formatFechaCorta(dateStr: string): string {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('es-ES', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    })
}

function formatHora(datetime: string): string {
    return new Date(datetime).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
}

onMounted(async () => {
    const today = new Date()
    const todayStr = today.toISOString().slice(0, 10)
    const weekEnd = new Date(today)
    weekEnd.setDate(weekEnd.getDate() + 6)
    const weekEndStr = weekEnd.toISOString().slice(0, 10)

    await Promise.allSettled([
        misAvisosApi
            .getAll({ finalizado: 0, pageSize: 50 })
            .then((r) => {
                avisosItems.value = r.items
                avisosTotal.value = r.totalCount
            })
            .catch(() => {
                errorAvisos.value = 'No se pudieron cargar los avisos.'
            })
            .finally(() => {
                loadingAvisos.value = false
            }),

        misPartesApi
            .getAll({ estado: 'abiertos', pageSize: 20 })
            .then((r) => {
                partesItems.value = r.items
                partesTotal.value = r.totalCount
            })
            .catch(() => {
                errorPartes.value = 'No se pudieron cargar los partes.'
            })
            .finally(() => {
                loadingPartes.value = false
            }),

        miAgendaApi
            .getAll({ fechaDesde: todayStr, fechaHasta: weekEndStr, pageSize: 50 })
            .then((r) => {
                agendaItems.value = r.items
            })
            .catch(() => {
                errorAgenda.value = 'No se pudo cargar la agenda.'
            })
            .finally(() => {
                loadingAgenda.value = false
            }),
    ])
})
</script>

<style scoped>
.text-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
